import {
  CodBookingStep,
  CodMarketing,
  CodResultsStep,
} from "../../../Components/LanderV2/cost-of-delay-calculator/CostOfDelayCalculator";
import {
  codDefaultDelayYears,
  codDefaultGrowthRate,
  codDelayOptions,
  codGrowthOptions,
  codMaxAge,
  codMinAge,
  codLeadPayload,
  delayOptionText,
} from "../../../Components/LanderV2/cost-of-delay-calculator/calculations";
import { LanderV2CampaignConfig } from "./types";
import { pensionFundQuestion, sharedSurveyProps } from "./shared";

const currencyMask = {
  maskType: "currency",
  maskSettings: { prefix: "£ ", max: 2100000000 },
  inputType: "tel",
  inputTextAlignment: "left",
};

export const costOfDelayCalculator: LanderV2CampaignConfig = {
  slug: "cost-of-delay-calculator",
  heading: "What could waiting to take your pension income cost?",
  subheading: "Add your details to calculate",
  proofLine:
    "See the cost of waiting using real annuity rates from leading UK providers.",
  accent: "magenta",
  surveyFormId: "cost-of-delay-calculator",
  funnelTracking: true,
  landingPageTheme: "Pense Variant 1",
  landingPageVariation: "cost-of-delay-calculator",
  landingPageVersion: "ux2-v1",
  // Partial leads on each step; the booking step sends the full lead, client and annuity as VLM3's quote step does.
  transformLeadPayload: codLeadPayload,
  forcedUrlParams: { utm_content: "cost_of_delay_calculator" },
  initialData: {},
  surveyProps: sharedSurveyProps,
  MarketingComponent: CodMarketing,
  steps: [
    {
      slug: "details",
      isEntry: true,
      completeText: "CONTINUE",
      initialData: { sex: "Male" },
      funnel: {
        start: "Started Form Page 1",
        complete: ["Completed Form Page 1"],
      },
      schemaPage: {
        name: "codDetails",
        elements: [
          // The same age field as VLM3, stored as dateOfBirth so it prefills across the landers.
          {
            type: "ageinput",
            name: "dateOfBirth",
            title: "Current age",
            isRequired: true,
            placeholder: "65",
            minAge: codMinAge,
            maxAge: codMaxAge,
          },
          {
            type: "radiogroup",
            elementCssClass: "radio-group-as-button",
            name: "sex",
            title: "Gender",
            isRequired: true,
            colCount: 2,
            defaultValue: "Male",
            choices: [
              { value: "Male", text: "Male" },
              { value: "Female", text: "Female" },
            ],
          },
          {
            ...pensionFundQuestion,
            title: "Pension pot size",
            placeholder: "100,000",
          },
        ],
      },
    },
    {
      slug: "assumptions",
      next: "results",
      completeText: "SEE MY RESULTS",
      disableTokenPrefill: true,
      initialData: {
        "cod.delayYears": codDefaultDelayYears,
        "cod.growthRate": codDefaultGrowthRate,
        "cod.potMode": "growth",
        "cod.contributionMode": "amount",
      },
      layoutClassName: "cod-assumptions-survey",
      funnel: {
        start: "Started Form Page 2",
        complete: ["Completed Form Page 2"],
      },
      stickyCompleteButton: true,
      heading: "How long will you wait, and how might your pot grow?",
      schemaPage: {
        name: "codAssumptions",
        elements: [
          {
            type: "dropdown",
            name: "cod.delayYears",
            title: "Delay taking your annuity by",
            isRequired: true,
            renderAs: "select",
            defaultValue: codDefaultDelayYears,
            choices: codDelayOptions.map((years) => ({
              value: years,
              text: delayOptionText(years),
            })),
          },
          {
            type: "dropdown",
            name: "cod.growthRate",
            title: "Assumed annual pot growth while waiting",
            description:
              "Based on a typical long-term pension growth assumption before charges.",
            descriptionLocation: "underInput",
            isRequired: true,
            renderAs: "select",
            defaultValue: codDefaultGrowthRate,
            choices: codGrowthOptions.map((rate) => ({
              value: rate,
              text:
                rate === codDefaultGrowthRate
                  ? `${rate}% (UK average)`
                  : `${rate}%`,
            })),
          },
          {
            type: "radiogroup",
            elementCssClass: "radio-group-as-button",
            name: "cod.potMode",
            title: "How should we estimate your pot?",
            description:
              "Choose whether to assume your pension pot only grows, or also receives regular monthly contributions while you wait.",
            descriptionLocation: "underInput",
            isRequired: true,
            colCount: 2,
            defaultValue: "growth",
            choices: [
              { value: "growth", text: "Growth only" },
              { value: "contributions", text: "Growth + contributions" },
            ],
          },
          // The retirement year the delay reaches, kept where VLM3's retirement date field reads it (month left blank).
          {
            type: "expression",
            name: "retirementYear",
            visible: false,
            expression: "currentYear() + {cod.delayYears}",
          },
          {
            type: "panel",
            name: "codContributions",
            visibleIf: "{cod.potMode} = 'contributions'",
            elements: [
              {
                type: "radiogroup",
                elementCssClass: "radio-group-as-button",
                name: "cod.contributionMode",
                title: "Contributions",
                titleLocation: "hidden",
                isRequired: true,
                colCount: 2,
                defaultValue: "amount",
                choices: [
                  { value: "amount", text: "Monthly amount" },
                  { value: "salary", text: "% of salary" },
                ],
              },
              {
                type: "text",
                name: "cod.monthlyContribution",
                title: "Monthly pension contribution",
                placeholder: "250",
                visibleIf: "{cod.contributionMode} = 'amount'",
                isRequired: true,
                ...currencyMask,
                validators: [
                  {
                    type: "numeric",
                    minValue: 0,
                    text: "Enter valid contribution details to estimate your future pot.",
                  },
                ],
              },
              {
                type: "text",
                name: "cod.salary",
                title: "Annual salary",
                placeholder: "35,000",
                visibleIf: "{cod.contributionMode} = 'salary'",
                isRequired: true,
                ...currencyMask,
                validators: [
                  {
                    type: "numeric",
                    minValue: 0,
                    text: "Enter valid contribution details to estimate your future pot.",
                  },
                ],
              },
              {
                type: "text",
                name: "cod.salaryPercent",
                title: "Monthly contribution (%)",
                placeholder: "8",
                startWithNewLine: false,
                visibleIf: "{cod.contributionMode} = 'salary'",
                isRequired: true,
                inputType: "number",
                validators: [
                  {
                    type: "numeric",
                    minValue: 0,
                    maxValue: 100,
                    text: "Enter valid contribution details to estimate your future pot.",
                  },
                ],
              },
              {
                type: "expression",
                name: "codMonthlyContributionPreview",
                title: "This adds per month while waiting",
                expression:
                  "iif({cod.contributionMode} = 'salary', {cod.salary} * {cod.salaryPercent} / 100 / 12, {cod.monthlyContribution})",
                displayStyle: "currency",
                currency: "GBP",
                maximumFractionDigits: 0,
              },
            ],
          },
        ],
      },
    },
    {
      slug: "results",
      Component: CodResultsStep,
      previous: "assumptions",
      completeText: "Finish",
      startOverOnNav: true,
      funnel: { view: "Quotes Viewed" },
      schemaPage: {
        name: "codResults",
        title: "Your cost of delay",
        elements: [],
      },
    },
    {
      slug: "booking",
      Component: CodBookingStep,
      previous: "results",
      completeText: "Book",
      startOverOnNav: false,
      funnel: { view: "Booking Page Viewed" },
      schemaPage: {
        name: "booking",
        title: "Book an appointment",
        elements: [],
      },
    },
  ],
};
